import React, { useEffect, useState } from "react";
import { Button, Empty, Spin, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { fetchBelegFile } from "./api";
import { saveFile } from "../../../../native/files";
import { isNative } from "../../../../native/platform";

/**
 * Shows the stored file through an authenticated blob URL: a PDF in an iframe, an image
 * inline, XML as text. The plain file URL needs the bearer token, so it cannot be a src.
 */
const BelegPreview = ({ belegId, mimeType, fileName }) => {
  const { t } = useTranslation("accounting");
  const [url, setUrl] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setUrl(null);
    setText("");
    setError("");
    if (!belegId) return undefined;
    setLoading(true);
    fetchBelegFile(belegId)
      .then(async res => {
        if (cancelled) return;
        const type = res.data.type || mimeType || "";
        if (/xml|text/.test(type)) {
          setText(await res.data.text());
        } else {
          objectUrl = URL.createObjectURL(res.data);
          setUrl(objectUrl);
        }
      })
      .catch(
        err =>
          !cancelled &&
          setError(
            err?.response?.status === 404
              ? t("The file is missing on the server.")
              : t("The file could not be loaded.")
          )
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [belegId]);

  if (loading) return <Spin style={{ display: "block", margin: "40px auto" }} />;
  if (error) return <Empty description={error} />;
  const isPdf = /pdf/.test(mimeType || "");
  const download = url ? (
    <Button
      icon={<DownloadOutlined />}
      // <a download> does nothing in the app WebViews; hand the file to the share sheet there.
      {...(isNative
        ? { onClick: async () => saveFile(await (await fetch(url)).blob(), fileName) }
        : { href: url, download: fileName })}
      style={{ marginBottom: 8 }}
    >
      {t("Download")}
    </Button>
  ) : null;
  if (text) {
    return (
      <pre
        style={{
          maxHeight: "70vh",
          overflow: "auto",
          fontSize: 11,
          background: "var(--ds-surface-muted)",
          padding: 8,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere"
        }}
      >
        {text}
      </pre>
    );
  }
  if (!url) return <Empty description={t("No preview")} />;
  return (
    <div>
      {download}
      {isPdf ? (
        <iframe
          title={fileName || "Beleg"}
          src={url}
          style={{ width: "100%", height: "70vh", border: "1px solid var(--ds-border-soft)" }}
        />
      ) : (
        <img
          src={url}
          alt={fileName || "Beleg"}
          style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
        />
      )}
      <Typography.Text
        type="secondary"
        className="acct-wrap"
        style={{ display: "block", marginTop: 4, fontSize: 12 }}
      >
        {fileName}
      </Typography.Text>
    </div>
  );
};

export default BelegPreview;
